import { PaginationButtonsProps } from 'components/shared'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { Page } from 'models'
import { PaginationProps, SortDirection } from 'utilities'
import { FilterRanges, TriggerSearchFunc } from './PaginationSearchBase.types'

export class PaginationSearchBase<SortProps, SearchProps, SearchResult> {
  private _initialPaginationProps: PaginationProps<SortProps>
  private _initialSearchProps: SearchProps
  private _initialSearchResult: SearchResult[]
  private _timeoutId: NodeJS.Timeout
  private _needTimeoutProps: Array<keyof SearchProps>
  triggerSearch: TriggerSearchFunc

  constructor (
    initialPaginationProps: PaginationProps<SortProps>,
    initialSearchProps: SearchProps,
    initialSearchResult: SearchResult[] = [],
    needTimeoutProps: Array<keyof SearchProps> = []
  ) {
    this._initialPaginationProps = initialPaginationProps
    this._initialSearchProps = initialSearchProps
    this._initialSearchResult = initialSearchResult
    this._needTimeoutProps = needTimeoutProps

    this.pagination = initialPaginationProps
    this.searchProps = initialSearchProps
    this.searchResults = initialSearchResult
  }

  @observable searchResults: SearchResult[]
  @observable pagination: PaginationProps<SortProps>
  @observable searchProps: SearchProps
  @observable filterRanges: FilterRanges = null
  @observable processing: boolean = true

  @action setFilterRanges = (filterRanges: FilterRanges) => {
    this.filterRanges = filterRanges
  }

  @action setSearchResults = (searchResults: SearchResult[]) => {
    this.searchResults = searchResults
  }

  @action setPaginationProps = (props: Partial<PaginationProps<SortProps>>) => {
    this.pagination = {
      ...this.pagination,
      ...props
    }
  }

  @action private _setSearchProps = (newProps: Partial<SearchProps>) => {
    this.searchProps = {
      ...this.searchProps,
      ...newProps
    }
  }

  @action setProcessing = (value: boolean) => {
    this.processing = value
  }

  handleSearchResultPaginationProps = (data: Page<any>) => {
    this.setPaginationProps({
      page: data.number,
      size: data.size,
      totalElements: data.totalElements,
      totalPages: data.totalPages
    })
  }

  onSearchPropsChange = (newProps: Partial<SearchProps>) => {
    this._setSearchProps(newProps)

    if (this._timeoutId) {
      clearTimeout(this._timeoutId)
    }

    if (this._needTimeoutProps.includes(Object.keys(newProps)[0] as any)) {
      this._timeoutId = setTimeout(() => this.triggerSearch({ resetPageNumber: true }), 800)
    } else {
      this.triggerSearch({ resetPageNumber: true })
    }
  }

  onPageSelect = (pageNumber: number) => {
    this.setPaginationProps({ page: pageNumber })
    this.triggerSearch()
  }

  handleSortDirectionChange = (sortProp: SortProps) => (sortDirection: SortDirection) => {
    this.setPaginationProps({
      sortProp,
      sortDirection
    })
    this.triggerSearch({ resetPageNumber: true })
  }

  @computed get areFilterRangesSet (): boolean {
    return this.filterRanges !== null
  }

  @computed get paginationInfo (): PaginationButtonsProps {
    return {
      elementsPerPage: this.pagination.size,
      selectedPageNumber: this.pagination.page,
      totalElements: this.pagination.totalElements,
      totalPages: this.pagination.totalPages
    }
  }

  // @computed get resultsCount (): number {
  //   return this.searchResults.length
  // }

  // @computed get hasResults (): boolean {
  //   return this.resultsCount > 0
  // }

  @observable isColumnMarked = computedFn(
    (sortProp: SortProps): boolean => {
      return this.pagination.sortProp === sortProp
    }
  )

  @observable getSortDirection = computedFn(
    (sortProp: SortProps): SortDirection => {
      return this.isColumnMarked(sortProp)
        ? this.pagination.sortDirection
        : SortDirection.ASC
    }
  )
}
